import React, { createContext, useContext, useEffect, useState } from "react";
import jwtDecode from "jwt-decode";
import { api } from "../utils/api";

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export default function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadPermissions = async () => {
    try {
      const res = await api.get("/permissions/me.php");
      const list = res?.permissions || [];
      setPermissions(list);
      localStorage.setItem("permissions", JSON.stringify(list));
      return list;
    } catch (e) {
      console.error("failed to load permissions", e);
      setPermissions([]);
      return [];
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userStr = localStorage.getItem("user");
    const permStr = localStorage.getItem("permissions");

    if (token && userStr) {
      try {
        const decoded = jwtDecode(token);
        if (decoded.exp * 1000 > Date.now()) {
          setUser(JSON.parse(userStr));
          if (permStr) {
            try { setPermissions(JSON.parse(permStr) || []); } catch { /* ignore */ }
          }
          // Always refresh in background so grants stay current
          loadPermissions().finally(() => setLoading(false));
          return;
        } else {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          localStorage.removeItem("permissions");
        }
      } catch {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        localStorage.removeItem("permissions");
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const res = await api.post("/auth/login.php", { email, password }, false);
    localStorage.setItem("token", res.token);
    localStorage.setItem("user", JSON.stringify(res.user));
    setUser(res.user);
    await loadPermissions();
    return res.user;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("permissions");
    setUser(null);
    setPermissions([]);
  };

  const hasRole = (...roles) => user && roles.includes(user.role);

  // Primary access check — Admin is always full, everyone else uses RBAC keys.
  const can = (permKey) => {
    if (!user) return false;
    if (user.role === "Admin") return true;
    if (!permKey) return true;
    return permissions.includes(permKey);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        permissions,
        login,
        logout,
        loading,
        hasRole,
        can,
        reloadPermissions: loadPermissions,
        apiRequest: api.get,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
